import PropTypes from 'prop-types'
import { renderContent } from '../utils/contentRenderer'

/**
 * One concept-illustration figure: a "Figure N" label, an optional citable id badge, the rendered
 * SVG, and the depicts caption. Shared by the student/educator `SkillContentTabs` and the admin
 * `KnowledgeNodeModal` so the markup can't drift between them. `showId` reveals the stable id for
 * citing the exact picture (admin always; educator only in citable views); students leave it off.
 */
export default function ConceptIllustrationFigure({ il, index, showId = false }) {
  // 1-based figure number. The server projects a contiguous `index`; the `index + 1` positional
  // fallback only matters for pre-#767 cached payloads that lack it.
  const figureNo = il.index ?? index + 1
  return (
    <figure className="force-light-mode bg-white rounded-lg border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <span className="font-semibold text-gray-700">Figure {figureNo}</span>
        {showId && il.id && (
          <code className="font-mono text-[10px] text-gray-400 truncate max-w-[55%]" title={il.id}>
            {il.id}
          </code>
        )}
      </div>
      <div
        className="flex justify-center overflow-x-auto"
        role="img"
        aria-label={il.altText || il.depicts || `Figure ${figureNo}`}
      >
        {il.svg ? (
          renderContent(il.svg, 'html')
        ) : il.image ? (
          // Raster illustration (e.g. a data:image/webp). The wrapper above carries the accessible
          // label, so the <img> itself is decorative (alt="") to avoid a duplicate announcement.
          <img src={il.image} alt="" className="max-w-full h-auto" />
        ) : null}
      </div>
      {il.depicts && (
        <figcaption className="mt-2 text-xs text-gray-600 text-center">{il.depicts}</figcaption>
      )}
    </figure>
  )
}

// Stable React key for an illustration in a list: the persistent id, or a unique-per-position
// `fig-N` fallback for legacy payloads with a null id (never a bare array index).
export const illustrationKey = (il, i) => il.id || `fig-${il.index ?? i + 1}`

ConceptIllustrationFigure.propTypes = {
  il: PropTypes.shape({
    id: PropTypes.string,
    index: PropTypes.number,
    svg: PropTypes.string,
    // Raster image data URI (data:image/...); rendered when there is no svg.
    image: PropTypes.string,
    depicts: PropTypes.string,
    altText: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  // When true, render the stable id badge so the figure can be cited precisely.
  showId: PropTypes.bool
}
